import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ATLAS_LOGO_URL, ATLAS_NAME } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Endpoint always 200 — only network error reaches here
      toast.error(err.response?.data?.detail || 'Could not send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex flex-col items-center gap-3 group" data-testid="forgot-brand">
            <div className="w-16 h-16 rounded-lg bg-white border border-[#EBE7E0] flex items-center justify-center overflow-hidden shadow-sm">
              <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-serif-display text-2xl text-[#14213D] tracking-tight">{ATLAS_NAME}</h1>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] mt-1">Editorial Archive</p>
            </div>
          </button>
        </div>

        <Card className="p-6 border border-[#EBE7E0]" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent-state">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="font-serif-display text-xl text-[#14213D] mb-2">Check your email</h2>
              <p className="text-sm text-[#5A6577] mb-6">
                If an account exists for <span className="font-medium text-[#14213D]">{email}</span>, we&apos;ve sent a password reset link.
                The link will expire in 24 hours.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7]"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="font-serif-display text-xl text-[#14213D]">Forgot your password?</h2>
                <p className="text-sm text-[#5A6577] mt-1">
                  Enter your email and we&apos;ll send you a link to reset it.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-[#5A6577] mb-1 block">Email</label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7]"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" /> Send reset link</>
                  )}
                </Button>
              </form>
              <div className="mt-5 text-center">
                <Link
                  to="/login"
                  className="text-xs text-[#5A6577] hover:text-[#14213D] inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
